import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { Box } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { useStyles } from './styles';
import { Calendar } from './components/Calendar/Calendar';
import { CalendarControls } from './components/CalendarControls/CalendarControls';
import GameScheduleEditor from './components/GameScheduleEditor/GameScheduleEditor';
import { useGameScheduleContext } from '../../context/GameScheduleContext';
import { useTranslation } from 'react-i18next';
import { LANG_TEXT } from '../../constants/language';
import Toast from '../../components/Toast/Toast';
import Loader from './components/Loader/Loader';
import { useNavigate } from 'react-router-dom';

export default function GameSchedule() {
  const classes = useStyles();
  const { gameSchedules, loading, updateGameSchedule } = useGameScheduleContext();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [gameStartTime, setGameStartTime] = useState('15:00');
  const [deliveryStartTime, setDeliveryStartTime] = useState('14:30');
  const [startMonthOffset, setStartMonthOffset] = useState(0);
  const { t } = useTranslation();
  const toast = useRef<{ type: 'error' | 'success'; content: string }>({ type: 'success', content: '' });
  const [openToast, setOpenToast] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!selectedDate) return;

    const dateStr = selectedDate.format('YYYY-MM-DD');
    const schedule = gameSchedules.find((s) => s.date === dateStr);

    if (schedule) {
      if (schedule.starttime_hh != null && schedule.starttime_mm != null) {
        setGameStartTime(`${String(schedule.starttime_hh).padStart(2, '0')}:${String(schedule.starttime_mm).padStart(2, '0')}`);
      } else {
        setGameStartTime('');
      }

      if (schedule.delivery_hh != null && schedule.delivery_mm != null) {
        setDeliveryStartTime(`${String(schedule.delivery_hh).padStart(2, '0')}:${String(schedule.delivery_mm).padStart(2, '0')}`);
      } else {
        setDeliveryStartTime('');
      }
    } else {
      setGameStartTime('');
      setDeliveryStartTime('');
    }
  }, [selectedDate, gameSchedules]);

  const months = useMemo(() => {
    const result = [];
    const startMonth = dayjs().startOf('month').add(startMonthOffset, 'month');
    for (let i = 0; i < 6; i++) {
      result.push(startMonth.add(i, 'month'));
    }
    return result;
  }, [startMonthOffset]);

  const gameScheduleDates = useMemo(() => {
    const dates: { [key: string]: { preorder?: boolean } } = {};
    gameSchedules.forEach((schedule) => {
      if (schedule.date) {
        dates[schedule.date] = {
          preorder: schedule.preorder,
        };
      }
    });
    return dates;
  }, [gameSchedules]);

  const selectedKey = useMemo(() => selectedDate?.format('YYYY-MM-DD') ?? '', [selectedDate]);
  const hasSchedule = useMemo(() => !!gameScheduleDates[selectedKey], [gameScheduleDates, selectedKey]);
  const selectedSchedule = useMemo(() => gameSchedules.find((schedule) => schedule.date === selectedKey), [gameSchedules, selectedKey]);
  const editorInitialValues = useMemo(
    () => ({
      gameStart: gameStartTime,
      deliveryStart: deliveryStartTime,
      preOrder: gameScheduleDates[selectedKey]?.preorder ?? false,
      sinceAmount: selectedSchedule?.since_amount ?? 1,
      sinceUnit: (selectedSchedule?.since_type as 'minute' | 'hour' | 'day') ?? 'hour',
      untilAmount: selectedSchedule?.until_amount ?? 2,
      untilUnit: (selectedSchedule?.until_type as 'minute' | 'hour' | 'day') ?? 'hour',
    }),
    [deliveryStartTime, gameScheduleDates, gameStartTime, selectedKey, selectedSchedule]
  );

  const handlePrevious = useCallback(() => setStartMonthOffset((s) => s - 6), []);
  const handleNext = useCallback(() => setStartMonthOffset((s) => s + 6), []);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorError, setEditorError] = useState<string>('');
  const handleEditClick = useCallback(() => {
    setEditorOpen(true);
    setEditorError('');
  }, []);

  const handleEditorClose = useCallback(() => {
    setEditorOpen(false);
    setEditorError('');
  }, []);

  const handleEditorConfirm = useCallback(
    async (data: {
      gameStart: string;
      deliveryStart: string;
      sinceAmount: number;
      sinceUnit: string;
      untilAmount: number;
      untilUnit: string;
      preOrderEnabled?: boolean;
    }) => {
      if (!data.gameStart || !data.deliveryStart) {
        setEditorError(t(LANG_TEXT.PLEASE_SELECT_BOTH_TIMES));
        return;
      }

      const [gameHH, gameMM] = data.gameStart.split(':').map(Number);
      const starttime_hh = gameHH;
      const starttime_mm = gameMM;

      const [deliveryHH, deliveryMM] = data.deliveryStart.split(':').map(Number);
      const delivery_hh = deliveryHH;
      const delivery_mm = deliveryMM;

      if (delivery_hh > starttime_hh || (delivery_hh === starttime_hh && delivery_mm > starttime_mm)) {
        setEditorError(`${t(LANG_TEXT.START_TIME_MUST_BE_AFTER_DELIVERY_TIME)}`);
        return;
      }

      const since_amount = data.sinceAmount;
      const since_type = data.sinceUnit.toLowerCase();
      const until_amount = data.untilAmount;
      const until_type = data.untilUnit.toLowerCase();

      try {
        if (selectedDate) {
          const dateStr = selectedDate.format('YYYY-MM-DD');
          await updateGameSchedule(dateStr, {
            date: dateStr,
            starttime_hh,
            starttime_mm,
            delivery_hh,
            delivery_mm,
            since_amount,
            since_type,
            until_amount,
            until_type,
            preorder: !!data.preOrderEnabled,
          });
        }

        toast.current = { content: t(LANG_TEXT.UPDATE_SUCCESS_MESSAGE), type: 'success' };
        setOpenToast(true);
        setGameStartTime(data.gameStart);
        setDeliveryStartTime(data.deliveryStart);
        setEditorError('');
        setEditorOpen(false);
      } catch (ex) {
        toast.current = { content: t(LANG_TEXT.UPDATE_FAILED_MESSAGE), type: 'error' };
        setOpenToast(true);
      }
    },
    [selectedDate, updateGameSchedule, t]
  );

  if (loading) {
    return (
      <Box component="main" className={classes.Dashboard}>
        <Loader />
      </Box>
    );
  }

  return (
    <Box component="main" className={classes.Dashboard}>
      <Calendar months={months} eventsData={gameScheduleDates} onDateSelect={setSelectedDate} onPrevious={handlePrevious} onNext={handleNext} />

      <CalendarControls
        selectedDate={selectedDate}
        gameStartTime={gameStartTime}
        deliveryStartTime={deliveryStartTime}
        onPreOrdersClick={() => navigate(`/pre_orders`)}
        onEditClick={handleEditClick}
        hasSchedule={hasSchedule}
      />

      <GameScheduleEditor
        open={editorOpen}
        onClose={handleEditorClose}
        selectedDate={selectedDate}
        initialValues={editorInitialValues}
        onConfirm={handleEditorConfirm}
        error={editorError}
      />

      <Toast
        content={toast.current?.content}
        variant={toast.current?.type}
        open={openToast}
        onClose={() => {
          setOpenToast(false);
        }}
      />
    </Box>
  );
}
